// Dialogs used by the invoice editor: emailing an invoice, and marking one paid.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Checkbox, Input, Select, Textarea, ModalShell } from '../lib/ui';
import { canAttachPdf, pdfBase64 } from '../lib/pdf';
import { invoiceDoc } from '../lib/tradeDocs';
import { PAID_METHODS, fillTemplate, formatGBP, invoiceFileName, invoiceTemplateVars } from '../lib/trade';
import { Label, errorText } from './TradeCommon';

const RECORD_INCOME_KEY = 'farmos.trade.recordIncome';

export function EmailInvoiceModal({ invoice, settings, account, onClose, onSent }) {
  const vars = invoiceTemplateVars(invoice);
  const [to, setTo] = useState(invoice.billTo?.email || account?.email || '');
  const [subject, setSubject] = useState(fillTemplate(settings.emailSubject, vars));
  const [message, setMessage] = useState(fillTemplate(settings.emailMessage, vars));
  const [attach, setAttach] = useState(canAttachPdf);
  const [copyMe, setCopyMe] = useState(!!settings.email);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const hasBank = settings.sortCode && settings.accountNumber;

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const pdf = attach ? await pdfBase64(invoiceDoc(invoice, settings)) : null;
      const { data } = await api.post(`/trade/invoices/${invoice._id}/email`, {
        to, subject, message, copyMe,
        ...(pdf ? { pdfBase64: pdf, filename: invoiceFileName(invoice) } : {}),
      });
      onSent(data);
    } catch (e) {
      setError(errorText(e));
      setSending(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={580} ariaLabel={`Email invoice ${invoice.number}`}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Email {invoice.number}</h2>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
        {formatGBP(invoice.total)} to {invoice.billTo?.businessName}.
        {settings.email ? ` Replies come to ${settings.email}.` : ''}
      </p>
      <div className="trade-form">
        <div>
          <Label hint="separate more than one with commas">To</Label>
          <Input type="email" multiple value={to} onChange={e => setTo(e.target.value)} placeholder="accounts@business.co.uk" autoFocus={!to} />
        </div>
        <div><Label>Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} /></div>
        <div>
          <Label>Message</Label>
          <Textarea rows={7} value={message} onChange={e => setMessage(e.target.value)} />
          <div className="trade-form__hint" style={{ marginTop: 6 }}>
            Under your message the email lists the invoice number, amount and due date
            {hasBank ? ', and your bank details.' : '. Add bank details in Invoice settings so it can say how to pay.'}
          </div>
        </div>
        <Checkbox
          checked={attach}
          onChange={e => setAttach(e.target.checked)}
          label={canAttachPdf ? 'Attach the invoice as a PDF' : 'Attach the PDF (only in the desktop app)'}
          style={canAttachPdf ? undefined : { opacity: 0.6, pointerEvents: 'none' }}
        />
        {settings.email && (
          <Checkbox checked={copyMe} onChange={e => setCopyMe(e.target.checked)} label={`Send a copy to ${settings.email}`} />
        )}
        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={send} disabled={sending || !to.trim()}>{sending ? 'Sending…' : 'Send'}</Button>
      </div>
    </ModalShell>
  );
}

export function PaidModal({ invoice, today, onClose, onConfirm }) {
  const [paidAt, setPaidAt] = useState(today);
  const [method, setMethod] = useState('bank_transfer');
  const [record, setRecord] = useState(() => {
    try { return localStorage.getItem(RECORD_INCOME_KEY) !== '0'; } catch { return true; }
  });
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    try { localStorage.setItem(RECORD_INCOME_KEY, record ? '1' : '0'); } catch { /* private mode */ }
    setSaving(true);
    const ok = await onConfirm({ paidAt, paidMethod: method, recordIncome: record });
    if (!ok) setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} width={440} ariaLabel={`Mark ${invoice.number} paid`}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{invoice.number} paid</h2>
      <div className="trade-form">
        <div className="inv-grid2">
          <div><Label>Paid on</Label><Input type="date" value={paidAt} max={today} onChange={e => setPaidAt(e.target.value || today)} /></div>
          <div>
            <Label>How</Label>
            <Select value={method} onChange={e => setMethod(e.target.value)}>
              {PAID_METHODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </Select>
          </div>
        </div>
        <Checkbox
          checked={record}
          onChange={e => setRecord(e.target.checked)}
          label={`Add ${formatGBP(invoice.total)} to the Finance page as income (trade sales)`}
        />
        <div className="trade-form__hint">Leave this off if the payment will come in with a bank statement import, so it isn't counted twice.</div>
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="success" onClick={confirm} disabled={saving}>{saving ? 'Saving…' : 'Mark paid'}</Button>
      </div>
    </ModalShell>
  );
}
